import { UseFormReturn } from 'react-hook-form'
import {
  BeneficiaryDetails,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
} from 'types/beneficiaries'
import { Currencies, WalletType } from 'types/types'
import { generateReference } from 'utils/utils'
import { PayForm } from '../Pay'

export const setFormDetails = (
  form: UseFormReturn<PayForm>,
  selectedWallet: WalletType,
  selectedBeneficiary: BeneficiaryDetails,
  enableBeneficiaryReference = false
) => {
  switch (selectedWallet?.currency?.code) {
    case Currencies.EUR:
      form?.setValue('iban', selectedBeneficiary?.fiat?.accountNumber)
      form?.setValue('bic', selectedBeneficiary?.fiat?.code)
      break
    case Currencies.GBP:
      form?.setValue('accountNumber', selectedBeneficiary?.fiat?.accountNumber)
      form?.setValue('sortCode', selectedBeneficiary?.fiat?.code)
      break
    case Currencies.NGN:
      form?.setValue('ngnAccountNumber', selectedBeneficiary?.fiat?.accountNumber)
      form?.setValue('bankCode', selectedBeneficiary?.fiat?.code)
      break
    case Currencies.USD:
      form?.setValue('usdAccountNumber', selectedBeneficiary?.fiat?.accountNumber)
      form?.setValue('abaRoutingNumber', selectedBeneficiary?.fiat?.code)
      break
    default:
      break
  }

  const businessName =
    selectedBeneficiary?.businessName ||
    `${selectedBeneficiary?.firstName} ${selectedBeneficiary?.lastName}`

  form?.setValue('countryCode', selectedBeneficiary?.addressCountry)
  form?.setValue('transferDestination', selectedBeneficiary?.transferDestination)
  form?.setValue('currencyCode', selectedBeneficiary?.currency)
  form?.setValue('beneficiaryEntityType', selectedBeneficiary?.entityType)

  if (selectedBeneficiary?.entityType === BeneficiaryEntityType.INDIVIDUAL) {
    form?.setValue('firstName', selectedBeneficiary?.firstName)
    form?.setValue('lastName', selectedBeneficiary?.lastName)
    form?.setValue('dateOfBirth', selectedBeneficiary?.dateOfBirth)
  } else {
    form?.setValue('businessName', businessName)
  }

  if (selectedBeneficiary?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL) {
    const { address } = selectedBeneficiary || {}
    const { bankAddress, intermediaryBank, bankName } = selectedBeneficiary?.fiat || {}

    bankAddress && form?.setValue('bankAddress', bankAddress)
    address && form?.setValue('address', address)
    intermediaryBank && form?.setValue('intermediaryBank', intermediaryBank)
    bankName && form?.setValue('bankName', bankName)
  }

  const paymentReference =
    enableBeneficiaryReference && !!selectedBeneficiary?.beneficiaryPaymentReference
      ? selectedBeneficiary?.beneficiaryPaymentReference
      : generateReference()

  form?.setValue('reference', paymentReference)
  form?.setValue('alias', selectedBeneficiary?.alias)
}

export const resetFields = (form: UseFormReturn<PayForm>) => {
  const fields: (keyof PayForm)[] = [
    'iban',
    'bic',
    'accountNumber',
    'sortCode',
    'ngnAccountNumber',
    'bankCode',
    'usdAccountNumber',
    'sortCode',
    'reference',
    'countryCode',
    'transferDestination',
    'currencyCode',
    'beneficiaryEntityType',
    'firstName',
    'lastName',
    'dateOfBirth',
    'businessName',
    'bankAddress',
    'address',
  ]

  fields.forEach(field => {
    form?.resetField(field, { defaultValue: null })
  })
}

export const getBeneficiaryOptions = (
  beneficiaries: BeneficiaryDetails[],
  beneficiaryTransferDestinationFilters: BeneficiaryTransferDestination[]
) => {
  return beneficiaries
    .filter(beneficiary =>
      beneficiaryTransferDestinationFilters.includes(beneficiary?.transferDestination)
    )
    .map(beneficiary => {
      const label =
        beneficiary?.businessName || `${beneficiary?.firstName} ${beneficiary?.lastName}`
      const aliasLabel = beneficiary?.alias ? `(${beneficiary.alias})` : ''
      return {
        icon: beneficiary?.currency?.toLowerCase() || '',
        label: `${label} ${aliasLabel} (${beneficiary?.entityType})`,
        value: beneficiary?.reference,
        secondLabel: ` ${beneficiary?.fiat?.code}`,
        secondLabelText: `${beneficiary?.fiat?.accountNumber}`,
      }
    })
}

export const getAccountNumberForCurrency = (currency: Currencies, formValues: PayForm): string => {
  switch (currency) {
    case Currencies.EUR:
      return formValues?.iban
    case Currencies.GBP:
      return formValues?.accountNumber
    case Currencies.NGN:
      return formValues?.ngnAccountNumber
    default:
      return ''
  }
}

export const getDetailsForCurrency = (currency: Currencies, formValues: PayForm) => {
  switch (currency) {
    case Currencies.EUR:
      return {
        bankAccountNumber: formValues?.iban,
        code: formValues?.bic,
      }
    case Currencies.GBP:
      return {
        bankAccountNumber: formValues?.accountNumber,
        code: formValues?.sortCode,
      }
    case Currencies.NGN:
      return {
        bankAccountNumber: formValues?.ngnAccountNumber,
        code: formValues?.bankCode,
      }
    case Currencies.USD:
      return {
        bankAccountNumber: formValues?.usdAccountNumber,
        code: formValues?.abaRoutingNumber,
      }
    default:
      return {}
  }
}
